import { useTransactionSummaryCalclation } from '@/InventoryControlModule/composables/useTransactionSummaryCalclation';
import { ICombinedOrderCondition, ICombinedOrderConditions } from '../entities/CombinedOrder';
import { ISummaryData } from '../entities/SummaryData';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IParentBusiness } from '../entities/Business';
import { IMonthlyBusinessData } from '../entities/MonthlyData';
import { convertStdInventoryIndexToDays } from '@/coreDomainsModule/inventoryIndices';
import { RecommendOrderFactory } from '../factories/RecommendOrderFactory';
import { IInventoryOrder } from '../entities/InventoryOrder';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * まとめ発注に関するサービス
 */
export class CombinedOrderService {
  // #region Fields

  private static readonly SIGNIFICANT_DIGITS = 8;
  private static readonly FIXED_POINT = 10 ** this.SIGNIFICANT_DIGITS;
  private static readonly WEIGHTS_LAMBDA: number = 0.01;

  private static readonly ALPHA_GROW = 1.05;
  private static readonly INITIAL_ALPHA = 1;
  private static readonly BEAM_WIDTH = 50;
  private static readonly MAX_STEP = 400;
  private static readonly TOP_K_MOVES_PER_STATE = 20;
  private static readonly ADDITIONAL_STEP = 50;

  private static readonly NO_CONDITION_ERROR_MODAL_PROP = {
    modalTitle: '条件を満たすまとめ発注を生成できません',
    modalDescription: '条件を変更して再度お試しください。',
  };
  private static readonly FAILED_TO_GENERATE_ERROR_MODAL_PROP = {
    modalTitle: 'まとめ発注案の生成に失敗しました',
    modalDescription: '条件を変更して再度お試しください。',
  };

  // #region Public functions

  /**
   * x を this.FIXED_POINT 倍して最近傍に丸め、再び this.FIXED_POINT で割って戻すことで量子化する
   * @param x - 丸めたい数
   * @returns - 小数点以下をthis.SIGNIFICANT_DIGITSの桁で丸めた数
   */
  public static quantize(x: number): number {
    return this.fromFixedPoint(this.toFixedPoint(x));
  }

  /**
   * まとめ発注案自動生成
   * @param combinedOrderConditions - まとめ発注の条件(Primary, Secondary)
   * @param summaryData - サマリーデータ
   * @param allBusinessData - 表示されている全ての取引データ
   * @param monthlyData - 月次データ
   */
  public static calculateCombinedOrder(
    combinedOrderConditions: ICombinedOrderConditions,
    summaryData: ISummaryData,
    allBusinessData: IParentBusiness[],
    monthlyData: Record<string, IMonthlyBusinessData>
  ): { maxTotalAmount: number; data: IInventoryOrder } | null {
    /**
     * エラーモーダルを表示する
     * @param prop - モーダルの要素
     * @param errorCode - エラーコード
     */
    const showErrorModal = (prop: { modalTitle: string; modalDescription: string }, errorCode?: string) => {
      const errorCodeDesc = errorCode ? `エラーコード: ${errorCode}` : '';
      showInfoModal({
        modalTitle: t(prop.modalTitle),
        modalDescription: `${t(prop.modalDescription)}${errorCodeDesc}`,
        backButtonLabel: t('OK'),
        isDisabledNextButton: true,
      });
    };

    try {
      const { targetCondition, monthDiff } = this.selectTargetCondition(combinedOrderConditions, summaryData, allBusinessData, monthlyData);
      if (targetCondition === null || monthDiff === null) {
        // 対象条件なしのためエラーで返す
        console.error('対象条件なし');
        showErrorModal(this.NO_CONDITION_ERROR_MODAL_PROP, CalculateCombinedOrderErrorCodeList.NoValidCondition);
        return null;
      }

      // metricsを用意する
      const referenceMetrics: ReferenceMetric[] = this.buildReferenceMetrics(allBusinessData, monthlyData, targetCondition, monthDiff);

      // 理想値導出
      const idealReferenceValue = this.calcIdealReferenceValue(referenceMetrics, targetCondition);

      if (idealReferenceValue === null || !Number.isFinite(idealReferenceValue)) {
        console.error('理想基準値導出できず');
        showErrorModal(this.FAILED_TO_GENERATE_ERROR_MODAL_PROP, CalculateCombinedOrderErrorCodeList.FailedToCalcIdealReferenceValue);
        return null;
      }

      // 理想発注数量を投入する
      referenceMetrics.forEach((referenceValue) => {
        referenceValue.idealOrderAmount = this.quantize(
          referenceValue.factorValue * Math.max(idealReferenceValue - referenceValue.referenceValue, 0)
        );
      });

      const initialAmountsByRounding = this.buildInitialAmountsByRounding(referenceMetrics, idealReferenceValue);

      const searchOption: BeamSearchOptimizeOption = {
        alphaGrow: this.ALPHA_GROW,
        initialAlpha: this.INITIAL_ALPHA,
        beamWidth: this.BEAM_WIDTH,
        maxStep: this.MAX_STEP,
        topKMovesPerState: this.TOP_K_MOVES_PER_STATE,
        additionalStep: this.ADDITIONAL_STEP,
      };

      const optimizedBeamState = this.searchOptimizedOrderAmounts(
        targetCondition,
        referenceMetrics,
        idealReferenceValue,
        initialAmountsByRounding,
        searchOption
      );
      const completedBeamState = Object.values(optimizedBeamState).filter((v): v is BeamState => v !== null && v.diff === 0);
      if (completedBeamState.length === 0) {
        console.error(`最適解導出失敗(completedBeamStateがない) optimizedBeamState: ${optimizedBeamState}`);
        showErrorModal(this.FAILED_TO_GENERATE_ERROR_MODAL_PROP, CalculateCombinedOrderErrorCodeList.FailedToCalcOptimizedAmounts);
        return null;
      }

      const mostOptimizedBeamState = completedBeamState.reduce((acc, cur) => {
        if (!acc) return cur;
        return acc.baseCost > cur.baseCost ? cur : acc;
      });
      if (mostOptimizedBeamState === null) {
        console.error(`最適解導出失敗(不明なエラー) completedBeamState: ${completedBeamState}`);
        showErrorModal(this.FAILED_TO_GENERATE_ERROR_MODAL_PROP, CalculateCombinedOrderErrorCodeList.UnexpectedError);
        return null;
      }

      const res = this.createCombinedOrderFromResult(targetCondition, referenceMetrics, monthDiff, mostOptimizedBeamState.x);

      return { maxTotalAmount: targetCondition.totalOrderAmount, data: res };
    } catch (e) {
      console.error(e);
      showErrorModal(this.FAILED_TO_GENERATE_ERROR_MODAL_PROP, CalculateCombinedOrderErrorCodeList.UnexpectedError);
      return null;
    }
  }

  // #region Private functions

  /**
   * 小数を排除するためにFIXED_POINTの値をかける
   * @param x - 整数化したい値
   * @returns - 整数化した値
   */
  private static toFixedPoint(x: number): number {
    if (!Number.isFinite(x)) {
      throw new Error('有限数以外の数値を変換しようとしています');
    }

    const scaled = Math.round(x * this.FIXED_POINT);

    if (!Number.isSafeInteger(scaled)) {
      throw new Error(`変換後の値が安全な整数ではありません: scaled=${scaled}, x=${x}, FIXED_POINT=${this.FIXED_POINT}`);
    }

    return Object.is(scaled, -0) ? 0 : scaled;
  }

  /**
   * 整数化した値を元の値に戻す
   * @param fp - 整数化された値
   * @returns - 元の値
   */
  private static fromFixedPoint(fp: number): number {
    if (!Number.isSafeInteger(fp)) {
      throw new Error(`変換前の数値が安全な整数ではありません: ${fp}`);
    }

    const y = fp / this.FIXED_POINT;

    return Object.is(y, -0) ? 0 : y;
  }

  /**
   * 最大公約数を求める
   * @param a - 数値1
   * @param b - 数値2
   * @returns - 数値1と数値2の最大公約数
   */
  private static gcd(a: number, b: number) {
    a = Math.abs(a);
    b = Math.abs(b);

    while (b !== 0) {
      const r = a % b;
      a = b;
      b = r;
    }
    return a;
  }

  /**
   * 配列の数値全ての最大公約数を求める
   * @param nums - 最大公約数を求めたい数値の配列
   * @returns - 数値配列全体の最大公約数(ただし、length = 0の時は0, length = 1の時は nums[0] を返す)
   */
  private static gcdArray(nums: number[]) {
    if (nums.length === 0) return 0;
    if (nums.length === 1) return nums[0];
    let g = 0;
    for (const n of nums) {
      g = this.gcd(g, n);
      if (g === 1) break;
    }
    return g;
  }

  /**
   * 評価関数
   * @param amount - 数量(ロット操作などを行なった後、整数化済み)
   * @param referenceValue - 参照する基準値集合
   * @param idealReferenceValue - 理想基準値
   */
  private static calcCost(amount: number, referenceValue: ReferenceMetric, idealReferenceValue: number) {
    return (
      Math.abs((referenceValue.expectedEndInventory + this.fromFixedPoint(amount)) / referenceValue.factorValue - idealReferenceValue) +
      this.WEIGHTS_LAMBDA / referenceValue.lotUnit
    );
  }

  /**
   * その集計がまとめ発注の考慮条件を満たしているかをチェックする
   * @param business - 取引データ
   * @param monthlyData - 月次データ
   * @param monthDiff - 当月からの月差分
   */
  private static isValidBusiness(business: IParentBusiness, monthlyData: Record<string, IMonthlyBusinessData>, monthDiff: number) {
    return (
      monthlyData[business.businessId][monthDiff].announce && business.stdInventoryIndices && (business.orderPackage || business.package)
    );
  }

  /**
   * 対象のまとめ発注条件を取得する
   * 条件は以下の通り
   *  - 発注合計数量を足した時の在庫回転率が上限を超えていない
   *  - 合計発注数量が取引全体のロット数量(発注単位 || 容量)の最大公約数で割り切れる
   * 対象の条件がない場合はNullを返す
   * @param combinedOrderConditions - まとめ発注条件一覧
   * @param summaryData - 集計サマリーデータ
   * @param allBusinessData - 全集計データ
   * @param monthlyData - 月次データ
   * @returns - 対象のまとめ発注条件と当月からの月の差分を返す。どれも条件に合わなかったらどっちもnull
   */
  private static selectTargetCondition(
    combinedOrderConditions: ICombinedOrderConditions,
    summaryData: ISummaryData,
    allBusinessData: IParentBusiness[],
    monthlyData: Record<string, IMonthlyBusinessData>
  ): { targetCondition: ICombinedOrderCondition | null; monthDiff: number | null } {
    const { getTransactionData } = useTransactionSummaryCalclation();

    let targetCondition: ICombinedOrderCondition | null = null;
    let monthDiff: number | null = null;

    for (const condition of Object.values(combinedOrderConditions)) {
      const condMonthDiff = DateService.calculateDiffMonths(DateService.convertIDateToDate(condition.desiredDeliveryDate), new Date());
      const expectedEndInventory = getTransactionData(summaryData, 'expectedEndInventory', condMonthDiff, true);
      const announce = getTransactionData(summaryData, 'announce', condMonthDiff, true);
      const turnOver = (expectedEndInventory + condition.totalOrderAmount) / announce;
      const allBusinessesLotUnit = allBusinessData
        .filter((business) => this.isValidBusiness(business, monthlyData, condMonthDiff))
        .map((business) => this.toFixedPoint(business.orderPackage || Number(business.package)));
      const allBusinessesLotsGcd = this.gcdArray(allBusinessesLotUnit);
      if (turnOver > condition.turnOverUpperLimit) {
        continue;
      }
      if (this.toFixedPoint(condition.totalOrderAmount) % allBusinessesLotsGcd !== 0) {
        continue;
      }
      targetCondition = condition;
      monthDiff = condMonthDiff;
      break;
    }
    return { targetCondition, monthDiff };
  }

  /**
   * referenceMetricsを生成する
   * @param allBusinessData - 全集計データ
   * @param monthlyData - 月次データ
   * @param targetCondition - 対象の条件
   * @param monthDiff - 当月からの月差分
   * @returns - ReferenceValue[]をreferenceValueが小さい順に並べたもの
   */
  private static buildReferenceMetrics(
    allBusinessData: IParentBusiness[],
    monthlyData: Record<string, IMonthlyBusinessData>,
    targetCondition: ICombinedOrderCondition,
    monthDiff: number
  ): ReferenceMetric[] {
    return allBusinessData
      .filter((business) => this.isValidBusiness(business, monthlyData, monthDiff))
      .map((business) => ({
        businessId: business.businessId,
        referenceValue:
          monthlyData[business.businessId][monthDiff].turnOver /
          convertStdInventoryIndexToDays(
            business.stdInventoryIndices!['標準在庫指数'].min,
            business.stdInventoryIndices!['標準在庫指数'].option.unit
          ),
        factorValue:
          monthlyData[business.businessId][monthDiff].announce *
          convertStdInventoryIndexToDays(
            business.stdInventoryIndices!['標準在庫指数'].min,
            business.stdInventoryIndices!['標準在庫指数'].option.unit
          ),
        expectedEndInventory: monthlyData[business.businessId][monthDiff].exceptedEndInventory,
        lotUnit: this.toFixedPoint(business.orderPackage || Number(business.package)),
        moq: targetCondition.isMOQConsidered ? this.toFixedPoint(business.MOQ ?? 0) : 0,
        idealOrderAmount: 0,
      }))
      .sort((a, b) => a.referenceValue - b.referenceValue);
  }

  /**
   * 理想基準値を導出する
   * @param referenceMetrics - 参照する基準値集合
   * @param targetCondition - 対象のまとめ発注条件
   * @returns - 理想基準値 (求まらなかったらnull)
   */
  private static calcIdealReferenceValue(referenceMetrics: ReferenceMetric[], targetCondition: ICombinedOrderCondition): number | null {
    // n件の集計に対して基準値が揃う様にまとめ発注の合計発注数量まで発注を行った際の基準値を保存する
    const orderedReferenceValueList: number[] = referenceMetrics.map((_, i) => {
      const slicedReferenceValues = referenceMetrics.slice(0, i + 1);
      const sumFactorValue = slicedReferenceValues.reduce((acc, refVal) => (acc += refVal.factorValue), 0);
      const sumRefFacVal = slicedReferenceValues.reduce((acc, refVal) => (acc += refVal.factorValue * refVal.referenceValue), 0);
      return (targetCondition.totalOrderAmount + sumRefFacVal) / sumFactorValue;
    });

    let idealReferenceValue = NaN;

    for (const [i, value] of orderedReferenceValueList.entries()) {
      if (!isNaN(value) && value >= referenceMetrics[i].referenceValue && value <= (referenceMetrics[i + 1]?.referenceValue ?? Infinity)) {
        idealReferenceValue = value;
        break;
      }
    }
    if (!Number.isFinite(idealReferenceValue)) {
      return null;
    }
    return idealReferenceValue;
  }

  /**
   * 初期解を作成する
   * 初期解は以下の3つについてMOQ制約に合うように作成し、ロットの最大公約数で割れるものだけを採用
   *  - 近傍丸め(round)
   *  - 天井丸め(ceil)
   *  - 底丸め(floor)
   * @param referenceMetrics - 参照する基準値集合
   * @param idealReferenceValue - 理想基準値
   * @returns - 初期解集合
   */
  private static buildInitialAmountsByRounding(referenceMetrics: ReferenceMetric[], idealReferenceValue: number): AmountsByRounding[] {
    /**
     * MOQ制約をチェックする。発注数が 0 < x < MOQ の場合、発注数をスコアに合わせて0 or MOQを超える最も小さいロット数量の整数倍 に変更する
     * @param x - 発注数量
     * @param referenceValue - 参照するReferenceValue
     */
    const checkMoqConstraints = (x: number, referenceValue: ReferenceMetric): number => {
      // 発注数量がMOQ以上 or 0ならそのまま返す
      if (x >= referenceValue.moq || x === 0) return x;
      const minAmountAtLeastMoq = Math.ceil(referenceValue.moq / referenceValue.lotUnit) * referenceValue.lotUnit;
      const minLotAtLeastMoqScore = this.calcCost(minAmountAtLeastMoq, referenceValue, idealReferenceValue);
      const zeroAmountScore = this.calcCost(0, referenceValue, idealReferenceValue);
      return minLotAtLeastMoqScore <= zeroAmountScore ? minAmountAtLeastMoq : 0;
    };

    return referenceMetrics.map((value): AmountsByRounding => {
      const base = this.toFixedPoint(value.idealOrderAmount) / value.lotUnit;
      return {
        rounded: checkMoqConstraints(Math.round(base) * value.lotUnit, value),
        ceiled: checkMoqConstraints(Math.ceil(base) * value.lotUnit, value),
        floored: checkMoqConstraints(Math.floor(base) * value.lotUnit, value),
      };
    });
  }

  /**
   * Beam Searchを用いて最適な発注数を計算する
   * @param targetCondition - 対象のまとめ発注条件
   * @param referenceMetrics - 参照する基準値情報
   * @param idealReferenceValue - 理想基準値
   * @param initialAmountsByRounding - 初期値の集合(丸め方別に複数存在)
   * @param option - Beam Searchのオプション
   */
  private static searchOptimizedOrderAmounts(
    targetCondition: ICombinedOrderCondition,
    referenceMetrics: ReferenceMetric[],
    idealReferenceValue: number,
    initialAmountsByRounding: AmountsByRounding[],
    option: BeamSearchOptimizeOption
  ): BeamStateByRounding {
    const stateByRounding: BeamStateByRounding = { rounded: null, ceiled: null, floored: null };
    const fixedTotalOrderAmount = this.toFixedPoint(targetCondition.totalOrderAmount);
    const targetBusinessesLotsGcd = this.gcdArray(referenceMetrics.map((value) => value.lotUnit));

    /**
     * MOQより大きい中で最も小さいロット整数倍になっている値を返す
     * @param referenceMetric - その発注の参照基準値オブジェクト
     * @returns - MOQより大きい中で最も小さいロット整数倍になっている値
     */
    const moqJump = (referenceMetric: ReferenceMetric): number => {
      if (referenceMetric.moq <= 0) return referenceMetric.lotUnit;
      return Math.ceil(referenceMetric.moq / referenceMetric.lotUnit) * referenceMetric.lotUnit;
    };
    /**
     * 値がMOQの制約を満たしているか確認する
     * @param referenceMetric - その発注の参照基準値オブジェクト
     * @param x - 発注数量
     * @returns - その値がMOQの制約を満たしているかどうか
     */
    const canBePositive = (referenceMetric: ReferenceMetric, x: number) => {
      return x === 0 || x >= referenceMetric.moq;
    };
    /**
     * 1ロット分増加させる(ただし、x=0の時はMOQまでジャンプ)
     * @param referenceMetric - その発注の参照基準値オブジェクト
     * @param x - 現在の発注数量
     */
    const inc = (referenceMetric: ReferenceMetric, x: number): number | null => {
      if (x === 0) {
        const y = moqJump(referenceMetric);
        return y;
      }
      return x + referenceMetric.lotUnit;
    };
    /**
     * 1ロット分減少させる(ただし、1ロット減らした時にMOQを下回る場合は0にする)
     * @param referenceMetric - その発注の参照基準値オブジェクト
     * @param x - 現在の発注数量
     */
    const dec = (referenceMetric: ReferenceMetric, x: number): number | null => {
      if (x === 0) return null;
      const y = x - referenceMetric.lotUnit;
      if (y === 0 || y < referenceMetric.moq) return 0;
      return y;
    };
    /**
     * Stateの中で、diffとxが全く同じものを排除する
     * @param states - Stateリスト
     * @returns - 重複を排除したState
     */
    const dedupCandidates = (states: BeamState[]): BeamState[] => {
      const best = new Map<string, BeamState>();
      for (const s of states) {
        const key = `${s.diff}|${s.x.join(',')}`;
        const prev = best.get(key);
        if (!prev || s.score < prev.score) best.set(key, s);
      }
      return Array.from(best.values());
    };

    /**
     * あるStateから次StepのStateを作成する
     * @param state - State
     * @param alpha - Scoreに対するDiffの影響力
     * @returns - 次ステップのState
     */
    const expandState = (state: BeamState, alpha: number): BeamState[] => {
      const out: BeamState[] = [];
      // 全ての対象取引に対して操作を行う
      for (let i = 0; i < referenceMetrics.length; i++) {
        const referenceValue = referenceMetrics[i];
        const oldX = state.x[i];

        const candidatesXi: number[] = [];

        // ロット数量分増加させた時の発注数量
        const yInc = inc(referenceValue, oldX);
        if (yInc !== null) candidatesXi.push(yInc);
        // ロット数量分減少させた時の発注数量
        const yDec = dec(referenceValue, oldX);
        if (yDec !== null) candidatesXi.push(yDec);

        for (const newXi of candidatesXi) {
          if (!canBePositive(referenceValue, newXi)) continue;
          const deltaSum = newXi - oldX;
          const newSum = state.sum + deltaSum;
          const newDiff = fixedTotalOrderAmount - newSum;

          // 新しいBaseCostは差分計算する
          const newBaseCost =
            state.baseCost -
            this.calcCost(oldX, referenceValue, idealReferenceValue) +
            this.calcCost(newXi, referenceValue, idealReferenceValue);

          // ScoreはBaseCostにDiffを倍率でかけたものを足していく
          const newScore = newBaseCost + alpha * Math.abs(newDiff);

          const newX = state.x.slice();
          newX[i] = newXi;

          out.push({ x: newX, sum: newSum, diff: newDiff, baseCost: newBaseCost, score: newScore });
        }
      }
      // Scoreが良い順にtopKMovesPerStateの数まで採用
      out.sort((a, b) => a.score - b.score);
      const trimmed = out.slice(0, option.topKMovesPerState);
      return trimmed;
    };

    // メイン処理
    // 初期解の数分（丸めタイプの数分）実行する
    for (const roundingType of ROUNDING_TYPE) {
      const initialX = initialAmountsByRounding.map((value) => value[roundingType]);
      const initialSum = initialX.reduce((acc, cur) => acc + cur, 0);
      const initialDiff = fixedTotalOrderAmount - initialSum;
      const initialBaseCost = initialX.reduce((acc, value, i) => acc + this.calcCost(value, referenceMetrics[i], idealReferenceValue), 0);

      if (Math.abs(initialDiff) % targetBusinessesLotsGcd !== 0) {
        continue;
      }

      let alpha = option.initialAlpha;

      let beam: BeamState[] = [
        {
          x: initialX.slice(),
          sum: initialSum,
          baseCost: initialBaseCost,
          diff: initialDiff,
          score: initialBaseCost + alpha * Math.abs(initialDiff),
        },
      ];

      // 現状の最適解
      let bestFeasible = initialDiff === 0 ? beam[0] : null;

      let maxStep = option.maxStep;

      let step = 0;
      // ループ処理開始
      for (; step < maxStep; step++) {
        const candidates: BeamState[] = [];
        // 次のBeamを作成する
        for (const state of beam) {
          const nexts = expandState(state, alpha);
          candidates.push(...nexts);
        }

        // 候補0ならもう何もできないのでやめ
        if (candidates.length === 0) break;

        // もしDiffが0かつ、現状の最適解よりbaseCostが低ければそれがより最適なので置き換える
        for (const state of candidates) {
          if (state.diff === 0 && (!bestFeasible || state.baseCost < bestFeasible.baseCost)) {
            bestFeasible = state;
            // 最初に最適解が見つかってから追加で50回まで試行する
            if (option.additionalStep) maxStep = step + option.additionalStep;
          }
        }

        // 重複排除
        const deduped = dedupCandidates(candidates);

        // BeamをScoreの良い順にbeamWidthの数で枝刈りする
        deduped.sort((a, b) => a.score - b.score);
        beam = deduped.slice(0, option.beamWidth);

        // Scoreに対するDiffの影響力を高めDiff=0に近づくようにする
        alpha *= option.alphaGrow;
      }

      if (bestFeasible) {
        stateByRounding[roundingType] = bestFeasible;
      } else {
        // TODO: ここで微調整入れてもいいかも
      }
    }

    return stateByRounding;
  }

  /**
   * 作成した情報を用いて発注情報を作成する
   * @param targetCondition - 対象のまとめ発注条件
   * @param referenceMetrics - 参照する基準値集合
   * @param monthDiff - 当月からの月差分
   * @param resultAmounts - 確定した発注情報
   */
  private static createCombinedOrderFromResult(
    targetCondition: ICombinedOrderCondition,
    referenceMetrics: ReferenceMetric[],
    monthDiff: number,
    resultAmounts: number[]
  ): IInventoryOrder {
    const amountWithBusinessIdList = resultAmounts.map((amount, i) => ({
      businessId: referenceMetrics[i].businessId,
      amount: this.fromFixedPoint(amount),
    }));
    return Object.fromEntries(
      amountWithBusinessIdList
        .filter((value) => value.amount !== 0)
        .map((value) => [
          value.businessId,
          {
            [monthDiff]: RecommendOrderFactory.create({
              amount: value.amount,
              crossDate: targetCondition.desiredDeliveryDate,
              status: OrderStatus.Recommended,
            }),
          },
        ])
    );
  }
}

/**
 * 基準値を保存しておく型定義
 * @param businessId - 集計ID(=aggregateBusinessId)
 * @param referenceValue - 基準値: 在庫回転率を標準在庫指数(min)で割ったもの
 * @param factorValue - 係数: 対象月内示と標準在庫指数(min)を掛けたもの
 * @param lotUnit - 発注単位||容量で、発注数はこれの整数倍である必要がある。なお、小数が入る可能性があるため事前にSCALEを掛けている
 * @param moq - MOQ（最小発注数量）で、発注数はこれより大きい必要がある。条件設定時にMOQを考慮するかどうかを選択でき、考慮しない場合は全て0にしておく。なお、小数が入る可能性があるため事前にSCALEを掛けている
 * @param idealOrderAmount - 理想発注数で、導出した理想基準値に合わせるために発注すべき値
 */
interface ReferenceMetric {
  businessId: string;
  referenceValue: number;
  factorValue: number;
  expectedEndInventory: number;
  lotUnit: number;
  moq: number;
  idealOrderAmount: number;
}

/**
 * ロットに合わせた最適化(Beam search)を行う際のオプション
 * Alphaとは、Score（State定義参照）に対するdiff重みの倍率のこと
 * Alphaが大きいほどdiffが大きい際にScoreが悪化する
 * @param alphaGrow - StepごとのAlphaの成長率
 * @param initialAlpha - Alphaの初期値
 * @param beamWidth - 枝刈り幅(Stateを幾つで切るか)
 * @param maxStep - 試行回数の上限
 * @param topKMovesPerState - Stateごとに次のStateをいくつ作るか
 * @param additionalStep - diff=0の解が見つかった後最適を探るために探索を行う回数
 */
interface BeamSearchOptimizeOption {
  alphaGrow: number;
  initialAlpha: number;
  beamWidth: number;
  maxStep: number;
  topKMovesPerState: number;
  additionalStep?: number;
}

/**
 * ロットに合わせた最適化(Beam search)のState
 * @param x - 現在の各集計に対する発注数量(ReferenceMetric[]のindexと同じindex)
 * @param sum - 現在の合計発注数量(sum x)
 * @param baseCost - State内各xから導出した基準値と理想基準値との差分を合計したもの 大きいほど悪い
 * @param diff - 規定の合計発注数量と現在の合計発注数量の差分
 * @param score - BaseCostにDiff分の重みをつけたもの 大きいほど悪い
 */
interface BeamState {
  x: number[];
  sum: number;
  baseCost: number;
  diff: number;
  score: number;
}

const ROUNDING_TYPE = ['rounded', 'ceiled', 'floored'] as const;

/**
 * 試行する丸めタイプ
 */
type RoundingType = (typeof ROUNDING_TYPE)[number];

/**
 * 丸めタイプ別の数量情報
 */
type AmountsByRounding = Record<RoundingType, number>;

/**
 * 丸めタイプ別の最適化状態
 */
type BeamStateByRounding = Record<RoundingType, BeamState | null>;

const CalculateCombinedOrderErrorCodeList = {
  NoValidCondition: 'CCO_001',
  FailedToCalcIdealReferenceValue: 'CCO_002',
  FailedToCalcOptimizedAmounts: 'CCO_003',
  UnexpectedError: 'CCO_999',
};
/**
 * まとめ発注案自動生成のエラーコードリストの型
 */
type CalculateCombinedOrderErrorCodeList = (typeof CalculateCombinedOrderErrorCodeList)[keyof typeof CalculateCombinedOrderErrorCodeList];
